import { ThemeProvider, createTheme, useTheme } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import { jaJP } from '@mui/material/locale';

import { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import Log from './Logger';
import { getFeatureToggle, GetFeatureToggleError } from './api';
import { type User, getUser } from './auth';

import { FeatureContext, FeatureToggle } from './components/FeatureContext';
import Home from './components/Pages/Home';
import Login from './components/Pages/Login';
import CustomSnackbarProvider from './custom-snackbar-provider';

const queryClient = new QueryClient();

function App() {
    const theme = useTheme();

    const [user, setUser] = useState<User>({} as User);
    const [isLoading, setIsLoading] = useState(true);
    const [status, setStatus] = useState<number | undefined>(undefined);
    const [featureToggle, setFeatureToggle] = useState<FeatureToggle>(new FeatureToggle());

    // 開発者ツールへconsole.logを無効化する
    disableConsoleLog();

    useEffect(() => {

        async function getLoginUser() {
            let user;

            try {
                user = await getUser();
            } catch (err) {
                setIsLoading(false);
                setUser({} as User);
                return;
            }

            setUser(user);

            // ログの設定
            try {
                Log.initialize(user);
                const logger = Log.getLogger();
                logger.ui.info('App', 'useEffect', 'GetUser:Login Success');
            } catch (error) {
                console.error(error);
            }

            // 権限の取得
            try {
                const features = await getFeatureToggle();
                setFeatureToggle(new FeatureToggle({ features: features }));
                setStatus(undefined);
            } catch (error) {
                console.error(error);
                if (error instanceof GetFeatureToggleError) {
                    setStatus(error.status);
                } else {
                    setStatus(undefined);
                }
            }

            setIsLoading(false);
        }

        getLoginUser();
    }, []);

    const RequireAuth = (props: any) => {

        if (user.userId !== undefined) {
            return props.component;
        }

        document.location = '/login'

        return props.component
    }

    if (isLoading) {
        return <LinearProgress />
    }

    return (
        <ThemeProvider theme={createTheme(theme, jaJP)}>
            <QueryClientProvider client={queryClient}>
                <CustomSnackbarProvider>
                    <BrowserRouter>
                        <Routes>
                            <Route path='/login' element={<Login />} />
                            <Route path='/' element={
                                <RequireAuth component={
                                    <FeatureContext.Provider value={featureToggle}>
                                        <Home user={user} isAllowed={featureToggle.allowed()} status={status} />
                                    </FeatureContext.Provider>
                                } />
                            } />
                            <Route path='*' element={<Navigate to='/login' replace />} />
                        </Routes>
                    </BrowserRouter>
                </CustomSnackbarProvider>
            </QueryClientProvider>
        </ThemeProvider>
    );
}

function disableConsoleLog() {
    if (!(window as any).console) {
        (window as any).console = {};
    }

    (window as any).console_ = (window as any).console;

    const methods: string[] = [
        'log', 'debug', 'warn', 'info'
    ];

    for (let i = 0; i < methods.length; i++) {
        (console as any)[methods[i]] = function () { };
    }
}

export default App;
